<template>
  <div
    v-if="liquid.ssEnabled"
    id="searchspring-content"
    :class="{loading: !searchspringLoaded}"
  />

  <!-- Mobile modal -->
  <overlay
    v-if="isMobile && !isMonogram"
    :namespace="namespace"
    :template="false"
    block-class="quick-add-overlay"
    direction="up"
    type="drawer"
    :teleport-content="true"
  >
    <!-- quickAddMobileProduct now comes from this component -->
    <template #body="{ isActive }">
      <quick-add-mobile
        v-cloak
        v-if="getComponentVisible('quick-add-mobile')"
        :is-active="isActive"
        :namespace="namespace"
        :preselected-options="preselectedOptions"
        :product="quickAddMobileProduct"
        :product-card-color="allColors && allColors[selectedColorIndex]"
        :wishlist="wishlist"
        @update-selected-variant="
          variant => $emit('update-selected-variant', variant)
        "
      />
    </template>
  </overlay>

  <div
    v-else-if="!liquid.ssEnabled"
    class="boost-listings critical-component-clear"
    :class="{'no-results': noProducts}"
  >
    <!-- Search bar -->
    <div
      v-if="isSearchPage"
      class="boost-filter-search container"
    >
      <h1
        class="boost-filter-search__search-title desktop"
        :class="{'no-results': noProducts}"
        v-text="searchTitle"
      />
      <div class="boost-filter-search___form-group form-group">
        <input
          ref="boostFilterSearchInput"
          v-model="inputQuery"
          class="boost-filter-search__search-input"
          type="text"
          :placeholder="'search'"
          @keyup.enter="submitSearch"
        >
        <button
          class="boost-filter-search__submit"
          @click="submitSearch"
          @keypress="submitSearch"
        >
          <span
            class="boost-filter-search__icon icon-button"
            v-html="$icon('misc.search')"
          />
        </button>
      </div>

      <h1
        class="boost-filter-search__search-title mobile"
        :class="{'no-results': noProducts}"
        v-text="searchTitle"
      />

      <h2
        v-if="!noProducts"
        class="boost-filter-search__product-count-title"
        v-text="totalProductTitle"
      />
    </div>

    <div
      v-if="!noProducts"
      class="boost-listings__grid grid vue"
      :class="{[$class('active')]: boostActive}"
    >
      <div
        v-for="(product, index) in products"
        :key="`product-card-${liquid.sectionId}-${product.id}`"
        class="product-card__wrapper"
        :class="product.doubleWidth && 'product-card__wrapper--double'"
      >
        <product-card
          v-if="product.id"
          :key="`${product.id}-${index}`"
          :index="index"
          :product="product"
          :block-class="'boost-listings__product-card'"
          :namespace="`boost-collection-${liquid.sectionId}`"
          :collection-url="liquid.collectionUrl"
          :preselected-color="preselectedColorsJSON[product.id]?.color"
        />

        <collection-content-banner
          v-else-if="product.promotion"
          :key="product.blockId"
          :collection-meta="product"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Boost listings (boost-listings)
 * -----------------------------------------------------------------------------
 * Boost listings component.
 *
 * @param {String} liquid.shop - Permanent shop domain.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.collectionTitle - Collection title.
 * @param {Number} liquid.productsPerPage - Products per page.
 * @param {Array} liquid.sortByOptions - Array of sort by options from settings.
 * @param {String} liquid.collectionUrl - Collection url.
 *
 */
import { nextTick, defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import * as productHelpers from '~/helpers/product'
import { queryProductById } from '~/helpers/query'
import { isPreorder } from '~/helpers/product'
import classes from '~/config/classes'
import ProductCard from '~async/product-card/product-card'
import CollectionContentBanner from '~async/collection-content-banner/collection-content-banner'

export default {
  name: 'BoostListings',

  components: {
    ProductCard,
    CollectionContentBanner,
    QuickAddMobile: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/quick-add-mobile/quick-add-mobile'
        ),
    }),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        ssEnabled: false,
        ssSchoolFilterLabel: null,
        collectionId: '',
        collectionTitle: '',
        collectionUrl: '',
        productsPerPage: '',
        sectionId: '',
        shop: '',
        sortByOptions: {},
        swatchBorders: '',
      }),
    },
    metafields: {
      type: Object,
      default: () => ({
        preselectedColors: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => ([]),
    },
    templateDict: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      type: 'filter',
      boostFilterSettings: '',
      choices: '',
      eventType: null,
      filter: [],
      noProducts: false,
      page: 1,
      products: [],
      totalProduct: 0,
      totalProductTitlePrefix: this.$string('search.products_label'),
      totalProductTitle: '',
      activeTagAttr: 'js-active-tag',
      isSearchPage: false,
      inputQuery: '',
      query: '',
      suggestedCollections: [],
      suggestedCollectionsLimit: 0,
      searchTitle: '',
      pageTitle: this.$string('search.title'),
      preselectedColorsJSON: this.metafields.preselectedColors ? JSON.parse(this.metafields.preselectedColors) : {},
      resultsTitlePrefix: this.$string('search.results_for'),
      noResultsTitlePrefix: this.$string('search.no_results_for'),
      updateRequest: {},
      boostActive: false,
      loadingStateData: '[js-boost-listing="loading"]',
      productCarouselData: '[data-component-handle="product-carousel"]',
      filterToggleData: '[js-boost-filter="toggle"]',
      filterSortByData: '[js-boost-filter="sortBy"]',
      state: 'default',
      isAddingToCart: false,
      quickAddMobileProduct: {},
      selectedColorIndex: 0,
      selectedOptions: [],
      searchspringLoaded: false,
      hasSchoolFilter: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
     ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      cart: (state) => state.cart.response,
    }),

    quickAddMobileProduct(){

      // When the product changes, I want to compute other properties, like selectedOptions
      /**
       * Set the initial options
       */
      this.selectedOptions = this.quickAddMobileProduct.options.map(
        option => {
          if (option.name.match(/color/gi)) {
            this.colorIndex = option.index
          }
          return { name: option.name, value: '' }
        },
      )

      return this.quickAddMobileProduct
    },

    selectedColorIndex(){
      return this.selectedColorIndex
    },

    isMonogram() {
      return this.quickAddMobileProduct?.tags?.filter(tag => tag.match(/monogram/gi)).length
    },

    /**
     * Compute all colors from product options.
     * @returns {Array}
     */
     allColors() {
      if (
        !Array.isArray(this.quickAddMobileProduct.options) ||
        this.quickAddMobileProduct.options[0].name.toLowerCase() !== 'color'
      ) {
        return null
      }

      const colors = this.quickAddMobileProduct.variants.reduce((agg, item) => {
        if(item?.selectedOptions.length){
          agg.push(item.selectedOptions[0].value.split(' / ')[0])
        }
        return agg
      }, []).map(color => color.trim())

      /**
       * Move preselected color to the beginning of the swatch colors.
       */
      if (this.preselectedColor && colors) {
        const colorIndex = colors.map(color =>
          color.toLowerCase(),
        ).indexOf(this.preselectedColor.toLowerCase())

        if (colorIndex > -1) {
          const color = colors.splice(colorIndex, 1)
          colors.unshift(color[0])
        }
      }
      return [...new Set(colors)]
    },

    /**
     * Set to retrieve filter data from the API.
     */
    buildFilterTree() {
      return '&build_filter_tree=true'
    },

    /**
     * Set to use the 'filter' endpoint for the API.
     */
    endpoint() {
      return window.cnvs.page.type === 'search' ? window.cnvs.page.type : 'filter'
    },

    /**
     * Returns results from drawer choices
     */
    drawerChoices() {
      return this.getChoices()
    },
  },

  watch: {
    drawerChoices(choices) {
      if (choices === this.choices) {
        return
      }

      this.choices = choices
      this.updateWindowLocationSearch(this.choices)
    },

    noProducts() {
      if (this.isSearchPage) {
        this.toggleNoResults()
      }
    },
  },

  created(){
    window.addEventListener("ss-dom-ready", () => {
      console.log('Searchspring DOM is READY!')
      this.searchspringLoaded = true
      const searchSpringPlaceholderElement = document.getElementById('searchspring-before-loading')
      if (searchSpringPlaceholderElement) {
        searchSpringPlaceholderElement.remove()
      }

      const schoolFilter = document.getElementById("ss__facet--tags_ss_school")
      if (schoolFilter
        && schoolFilter.classList.contains("ss__collapsed")
      ) {
        console.log('has closed school filter')
        this.hasSchoolFilter = true;
        schoolFilter.getElementsByClassName("ss__title")?.[0]?.click()
        const schoolFilterInput = document.getElementById("ss__list--search-input--Schools")
        if (schoolFilterInput && this.liquid.ssSchoolFilterLabel) {
          schoolFilterInput.placeholder = this.liquid.ssSchoolFilterLabel
        }

        if (this.isMobile) {
          const filters = document.getElementsByClassName("boost-filter")
          console.log('filters', filters)
          if (filters?.length) {
            filters[0].style.display = 'none'
          }
        }
      }

    })
  },

  mounted() {
    // const searchSpringMainElement = document.getElementById('searchspring-before-loading')
    // searchSpringMainElement.innerHTML = this.$icon('misc.loading')

    if(!this.liquid.ssEnabled){
      this.getSearchParams()
      this.setEventListeners()
      this.setSwatches()
      this.initSearchInput()
      this.toggleNoResults()
    }

    let observer = new MutationObserver(mutationRecords => {
      const addToCartButtons = []
      const singleAddToCartButtons = document.querySelectorAll("div#searchspring-content button.quick-add__button.quick-add__add-cart")
      const sizeAddToCartButtons = document.querySelectorAll("div#searchspring-content button.quick-add__size-button")
      const mobileAddToCartButtons = document.querySelectorAll("div#searchspring-content button.product-card__quick-add.quick-add")

      addToCartButtons.push(...Array.from(singleAddToCartButtons),
                   ...Array.from(sizeAddToCartButtons),
                   ...Array.from(mobileAddToCartButtons))

      addToCartButtons.forEach(button => {

        const variantId = Number(button.getAttribute('data-variant-id'));
        const productId = Number(button.getAttribute('data-uid'));

        if (this.templateDict[productId] === 'personalizable') {
          button.disabled = true
          button.title = "You need to go to this product's page to add it to your cart"

          const parent = button.closest(".product-card__quick-add.quick-add")
          if (parent) {
            parent.style.display = 'none'
          }

        }

        (async () => {
          const product = await queryProductById(productId)
          const variant = product?.variants.find(v => v.id === variantId);

          if(product && variant && isPreorder(product, variant)){
            button.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();

              window.location = product?.url || window.location.href;
            }
          }else{

            button.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Im adding to Cart from LISTINGS!');
              if(!this.isMobile){

                (async () => {
                  const product = await queryProductById(productId);
                  this.handleAddToCart(variantId, product, button);
                })().catch(e => {
                    console.log(e);
                })

              }else{

                (async () => {
                  const product = await queryProductById(productId);
                  this.quickAddMobileProduct = product;
                  this.handleOpenMobileQuickAdd(product);
                })().catch(e => {
                    console.log(e)
                })

              }
            }

          }
        })().catch(e => {
            console.log(e)
        })
      })

      const addToWishlistButtons = Array.from(document.getElementsByClassName("product-card__wishlist wishlist-button product-card__action"))

      addToWishlistButtons.forEach(button => {

        const variantId = Number(button.getAttribute('data-variant-id'));
        const productId = Number(button.getAttribute('data-uid'));

        (async () => {
          const product = await queryProductById(productId)

          if(product?.tags.includes('preorder2024') || product?.tags.includes('purple-dot-live-waitlist')){
            button.classList.add('remove-wishlist-button');
          }
        })().catch(e => {
            console.log(e)
        })

        if(this.active(variantId)){
          button.classList.add('wishlist-active')
        }

        button.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.openWishlistModal(variantId, productId, button)
        }
      })

      const swatchButtons = Array.from(document.querySelectorAll("button.color-swatch.boost-listings__product-card__product-card-color-swatch"))

      swatchButtons.forEach(swatchButton => {
        const color = swatchButton.getAttribute('data-color')
        if(color?.toLowerCase() === "white"){
          const swatchElement = swatchButton.querySelector("span.color-swatch__swatch")
          swatchElement?.classList?.add("color-swatch__swatch--white")
        }

        swatchButton.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          const productId = swatchButton.parentNode.parentNode.parentNode.parentNode.nextSibling.getAttribute('data-uid');

          (async () => {
            const product = await queryProductById(productId)
            this.quickAddMobileProduct = product
            const index = product.options[0].values.indexOf(color)
            this.selectedColorIndex = index
          })().catch(e => {
              console.log(e)
          })

        }
      })

    })

    const elem = document.getElementById('searchspring-content')

    observer.observe(elem, {
      childList: true,
      subtree: true,
      characterDataOldValue: true,
      attributes: true,
      attributeFilter: ['data-variant-id'],
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      updateChoices: 'boost-listings/updateChoices',
      updateFilters: 'boost-listings/updateFilters',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'boost-listings/getChoices',
      getFilters: 'boost-listings/getFilters',
    }),

    active(variantId) {
      const { wishlist_variant_ids } = this.cart.attributes

      if (!wishlist_variant_ids || !variantId) {
        return false
      }

      const variants = JSON.parse(JSON.stringify(wishlist_variant_ids))
      return variants.indexOf(variantId) > -1
    },

    /**
     * Update the wishlist modal with the selected variant / product and open
     * the modal.
     */
     openWishlistModal(variantId, productId, button) {
      if (variantId === '') {
        return
      }

      this.updateModal(variantId, productId, button)

      if (!this.active(variantId)) {
        setTimeout(()=>{
          this.openOverlay({
            component: 'gift-reggie',
            ignoreDismissed: true,
            namespace: 'giftReggie',
          })
        }, 600)
      }

    },

    updateModal(variantId, productId, button) {
      cnvs.EventBus.emit('giftReggie:update', {
        variantId,
        productId,
        action: this.active(variantId) ? 'remove' : 'add',
        button: button,
      })
    },

    /**
     * Add to cart and open the cart drawer.
     */
     async handleAddToCart(variantId, product, button) {
      if (this.isAddingToCart) {
        return
      }
      const buttonOriginalInnerHtml = button.innerHTML
      try {
        const variant = product.variants.find(variant => variant.id === variantId)

        const properties = productHelpers.getLineItemProperties(product, variant)

        this.isAddingToCart = true

        button.innerHTML = this.$icon('misc.loading')

        await this.addItem([
          {
            id: variantId,
            quantity: 1,
            properties,
            variant: variant,
            product: product,
          },
        ])

        let type = 'filter'

        if (window.cnvs.page.type === 'search') {
          type = 'search'
        }

        this.type = type

        this.isAddingToCart = false

        button.innerHTML = buttonOriginalInnerHtml

        this.openOverlay({
          namespace: 'cartDrawer',
          ignoreDismissed: true,
          component: 'cart-drawer',
        })

        this.$gtmTrack('AddtoCart', this.$variable('analytics.quickAdd'))
      }
      catch (error) {
        this.isAddingToCart = false
        button.innerHTML = buttonOriginalInnerHtml
        console.error('[QuickAddDesktop]', error)
      }
    },

    /**
     * Open mobile quick add and track event
     */
     handleOpenMobileQuickAdd(product) {

      this.openOverlay({
        component: 'quick-add-mobile',
        ignoreDismissed: true,
        namespace: '',
      })

      this.$gtmTrack('open_quick_view', 'product_tile_quick_view', {
        product: {
          title: product.title,
          handle: product.handle,
        },
      })
    },

    /**
     * Manually clear the listings loading state.
     */
    clearLoadingState() {
      const loadingState = document.querySelector(this.loadingStateData)
      if (!loadingState) {
        return
      }

      loadingState.classList.add(classes.hidden)
      this.boostActive = true
    },

    /**
     * Callback after user choices emitted from facets to filter.
     * @param {Object} response - Response object with choices update.
     */
    choicesUpdated(response) {
      const { requestParam, action } = response
      const pageChoice = this.choices.split('&').filter((choice) => choice.includes('page'))[0]
      const sortChoice = this.choices.split('&').filter((choice) => choice.includes('sort'))[0]

      if (pageChoice !== undefined) {
        this.choices = this.choices.split(`&${pageChoice}`).join('')
      }

      if (requestParam.includes('sort') && sortChoice !== undefined) {
        this.choices = this.choices.split(`&${sortChoice}`).join('')
      }

      if (action === 'add') {
        this.choices = this.choices += requestParam

      } else {
        this.choices = this.choices.split(requestParam).join('')
      }

      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
      this.updateQueryFilter()
    },

    /**
     * Clear choices when 'clear all' button is clicked.
     */
    clearChoices() {
      this.choices = ''
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
      this.updateQueryFilter()
    },

    /**
     * Check window.location.search on load to see if
     * filter choices or search query exist before making API call.
     */
    getSearchParams() {
      if (!window.location.search.includes('?')) {
        this.choices = ''
        this.updateChoices(this.choices)
        this.updateQueryFilter()
        return
      }

      const params = window.location.search.split('?')[1]

      if (params.includes('q=') && !params.includes('&')) {
        this.query = params.split('q=')[1]
        this.inputQuery = decodeURIComponent(this.query)
        this.choices = ''
      } else if (params.includes('q=') && params.includes('&')) {
        this.query = params.split('q=')[1].split('&')[0]
        this.inputQuery = decodeURIComponent(this.query)
        this.choices = params.replace(`q=${this.query}`, '')
      } else if (!params.includes('q=') && params.includes('&')) {
        this.query = ''
        this.choices = params
      }

      this.updateQueryFilter()
    },

    /**
     * Updates the choices if pagination yeilds no results.
     * @param {Array} products - data from API results
     */
    handleEmptyPagination(products) {
      const currentPagination = this.choices.split('&').filter((choice) => {
        return choice.includes('page=')
      })

      if (currentPagination.length && products.length < 1) {
        this.choices = this.choices.replace(`&${currentPagination[0]}`, '')
        this.updateChoices(this.choices)
        this.updateWindowLocationSearch(this.choices)
        this.updateQueryFilter()
        return
      }
    },

    /**
     * Initialize on search page.
     */
    initSearchInput() {
      if (window.cnvs.page.type === 'search') {
        this.isSearchPage = true
      }
    },

    /**
     * Place content banners in line with product cards
     */
    injectInGridBanners() {
      const injectedPositions = []

      if (!this.blocks.length) {
        return
      }

      this.blocks.forEach((block) => {
        if (
          injectedPositions.includes(block.position) ||
          (block.doubleWidth && (block.position % 2 === 0))
        ) {
          return
        }

        const samePositionBlocks = this.blocks.filter((_block) => {
          return (
            _block.position === block.position &&
            _block.blockId !== block.blockId &&
            _block.collection !== ''
          )
        })

        if (block.collection === '' && samePositionBlocks.length > 0) {
          return
        }

        this.products.splice(block.position - 1, 0, block)
        injectedPositions.push(block.position)
      })
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.onpopstate = () => this.getSearchParams()
    },

    /**
     * Set swatch borders to global variables.
     */
    setSwatches() {
      const swatchBorders = this.liquid.swatchBorders.split(',').map((swatch) => {
        return swatch.trim().replaceAll(' ', '-').toLowerCase()
      })
      window.cnvs = window.cnvs || {}
      window.cnvs.boostSwatchBorders = swatchBorders
    },

    /**
     * Applies the input data to the query  and update window location params.
     */
    submitSearch() {
      this.query = this.inputQuery

      nextTick(() => {
        this.updateWindowLocationSearch()
        this.updateQueryFilter()
      })
    },

    /**
     * When there are no results this will:
     * - Display an available product carousel
     * - Hide the filter toggle if no choices were selected
     */
    toggleNoResults() {
      const productCarousel = document.querySelector(this.productCarouselData)
      const filterToggle = document.querySelector(this.filterToggleData)
      const fitlerSortBy = document.querySelector(this.filterSortByData)

      const hasFilter = this.filter.filter((choice) => {
        return choice.values?.length
      })

      if (this.noProducts && !this.choices || this.noProducts && hasFilter.length === 0) {
        filterToggle.classList.add(classes.hidden)
        fitlerSortBy.classList.add(classes.hidden)
      } else {
        filterToggle.classList.remove(classes.hidden)
        fitlerSortBy.classList.remove(classes.hidden)
      }

      if (!productCarousel) {
        return
      }

      if (this.noProducts) {
        productCarousel.classList.remove(classes.hidden)
      } else {
        productCarousel.classList.add(classes.hidden)
      }
    },

    /**
     * Updates boost request.
     */
    updateQueryFilter() {
      this.updateRequest = {
        choices: this.choices,
        query: this.query,
      }
    },

    /**
     * Updates the search title and product count title.
     */
    updateTitles() {
      if (this.isSearchPage && this.query) {
        const decodedQuery = decodeURIComponent(this.query)

        if (this.noProducts) {
          this.searchTitle = `${this.noResultsTitlePrefix} "${decodedQuery}"`
        } else {
          this.searchTitle = `${this.resultsTitlePrefix} "${decodedQuery}"`
        }
      } else {
        this.searchTitle = decodeURIComponent(this.pageTitle)
      }

      if (this.totalProduct > 0) {
        this.totalProductTitle = `${this.totalProductTitlePrefix} (${this.totalProduct})`
      } else {
        this.totalProductTitle = ''
      }
    },

    /**
     * Update window location search and browser history after choices or search query updated.
     */
    updateWindowLocationSearch() {
      const choices = this.choices
      const query = this.query !== '' ? `q=${this.query}` : ''

      let url = window.location.href.replace(window.location.search, `?${query}${choices}`)

      if (window.location.search === '') {
        url = window.location.href.replace(window.location.href, `${window.location.href}?${query}${choices}`)
      }

      if (!url.includes('&') && !url.includes('q=')) {
        url = url.replace('?', '')
      }

      window.history.pushState({}, '', url)
    },

    /**
     * Get all relevant collections from API response data.
     * @param {Object} products - product object from API.
     */
    updateCollectionResults(products) {
      const bucket = []
      const collections = []

      products.forEach((product) => {
        if (product.collections.length > 0) {
          product.collections.forEach((collection) => {
            if (!bucket.includes(collection.handle)) {
              bucket.push(collection.handle)
              collections.push({ handle: collection.handle, title: collection.title })
            }
          })

        }
      })

      const searchTermParts = this.query.split(' ')
      const firstTerm = searchTermParts[0] ? searchTermParts[0].toLowerCase() : false
      const secondTerm = searchTermParts[1] ? searchTermParts[1].toLowerCase() : false
      const thirdTerm = searchTermParts[2] ? searchTermParts[2].toLowerCase() : false

      collections.forEach((collection, i) => {
        if (collection.handle.toLowerCase().includes(firstTerm) ||
          collection.handle.toLowerCase().includes(secondTerm) ||
          collection.handle.toLowerCase().includes(thirdTerm)
          ) {
          const collectionMatch = collection
          collections.splice(i, 1)
          collections.unshift(collectionMatch)
        }
      })

      if (collections.length > this.suggestedCollectionsLimit) {
        this.suggestedCollections = collections.slice(0, this.suggestedCollectionsLimit)
        return
      }

      this.suggestedCollections = collections
    },
  },
}
</script>

<style lang="scss">
  @import './boost-listings';
  @import './searchspring-filter';
</style>
